import { faDownload } from "@fortawesome/pro-light-svg-icons";
import { VaporToolbar } from "@vapor/react-custom";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { VaporIcon } from "@vapor/react-icons";
import { Button, ButtonGroup, CircularProgress } from "@vapor/react-material";
import Box from "@vapor/react-material/Box";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDownloadTaxSubmissionOutcome } from "../../../../../core/api/use-download-tax-submission-outcome/useDownloadTaxSubmissionOutcome";
import { useDownloadTaxSubmissionPdfOutcome } from "../../../../../core/api/use-download-tax-submission-pdf-outcome/useDownloadTaxSubmissionPdfOutcome";
import { GovernmentAgencyType } from "../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { downloadFile } from "../../../../../core/utils/downloadFile";
import { TaxSubmissionOutcomePreviewer } from "../tax-submission-outcome-previewer/TaxSubmissionOutcomePreviewer";

interface TaxSubmissionOutcomePreviewProps {
    availableOutcomeTypes: string[];
    governmentAgencyType: GovernmentAgencyType;
    onChangeOutcomeType: (outcomeType: string) => void;
    taxSubmissionId: string;
    selectedOutcomeType: string;
    taxPayer?: string;
}

export const TaxSubmissionOutcomePreview: React.FC<
    TaxSubmissionOutcomePreviewProps
> = ({
    availableOutcomeTypes,
    governmentAgencyType,
    onChangeOutcomeType,
    taxSubmissionId,
    selectedOutcomeType,
    taxPayer,
}) => {
    return governmentAgencyType === GovernmentAgencyType.ade ? (
        <TaxSubmissionOutcomePreviewerAde
            availableOutcomeTypes={availableOutcomeTypes}
            governmentAgencyType={governmentAgencyType}
            onChangeOutcomeType={onChangeOutcomeType}
            taxSubmissionId={taxSubmissionId}
            selectedOutcomeType={selectedOutcomeType}
            taxPayer={taxPayer}
        />
    ) : (
        <TaxSubmissionOutcomePreviewerOther
            availableOutcomeTypes={availableOutcomeTypes}
            governmentAgencyType={governmentAgencyType}
            onChangeOutcomeType={onChangeOutcomeType}
            taxSubmissionId={taxSubmissionId}
            selectedOutcomeType={selectedOutcomeType}
            taxPayer={taxPayer}
        />
    );
};

const TaxSubmissionOutcomePreviewerAde: React.FC<
    TaxSubmissionOutcomePreviewProps
> = ({
    availableOutcomeTypes,
    governmentAgencyType,
    onChangeOutcomeType,
    taxSubmissionId,
    selectedOutcomeType,
    taxPayer,
}) => {
    const { data: file, isLoading } = useDownloadTaxSubmissionPdfOutcome(
        taxSubmissionId,
        [selectedOutcomeType],
        taxPayer,
    );

    return (
        <TaxSubmissionOutcomePreviewInner
            availableOutcomeTypes={availableOutcomeTypes}
            governmentAgencyType={governmentAgencyType}
            onChangeOutcomeType={onChangeOutcomeType}
            selectedOutcomeType={selectedOutcomeType}
            taxPayer={taxPayer}
            loading={isLoading}
            file={file}
        />
    );
};

const TaxSubmissionOutcomePreviewerOther: React.FC<
    TaxSubmissionOutcomePreviewProps
> = ({
    availableOutcomeTypes,
    governmentAgencyType,
    onChangeOutcomeType,
    taxSubmissionId,
    selectedOutcomeType,
    taxPayer,
}) => {
    const { data: file, isLoading } = useDownloadTaxSubmissionOutcome(
        taxSubmissionId,
        selectedOutcomeType,
        taxPayer,
    );
    return (
        <TaxSubmissionOutcomePreviewInner
            availableOutcomeTypes={availableOutcomeTypes}
            governmentAgencyType={governmentAgencyType}
            onChangeOutcomeType={onChangeOutcomeType}
            selectedOutcomeType={selectedOutcomeType}
            taxPayer={taxPayer}
            loading={isLoading}
            file={file}
        />
    );
};
const TaxSubmissionOutcomePreviewInner: React.FC<{
    availableOutcomeTypes: string[];
    governmentAgencyType: GovernmentAgencyType;
    onChangeOutcomeType: (outcomeType: string) => void;
    selectedOutcomeType: string;
    taxPayer?: string;
    loading: boolean;
    file?: { content: Uint8Array; type: string };
}> = ({
    availableOutcomeTypes,
    governmentAgencyType,
    onChangeOutcomeType,
    file,
    selectedOutcomeType,
    taxPayer,
    loading,
}) => {
    const { t } = useTranslation();
    const [isFileDownloading, setIsFileDownloading] = useState(false);

    const handleActiveOutcomeTypeChange = useCallback(
        (outcomeType: string) => {
            onChangeOutcomeType(outcomeType);
        },
        [onChangeOutcomeType],
    );

    const handleDownload = async () => {
        if (file) {
            setIsFileDownloading(true);
            try {
                const fileName = taxPayer
                    ? `${selectedOutcomeType}_${taxPayer}`
                    : selectedOutcomeType;

                await downloadFile(file.content, fileName, file.type);
            } catch (error) {
                console.error("Download failed:", error);
            } finally {
                setIsFileDownloading(false);
            }
        }
    };

    const outcomeButtons = useMemo(() => {
        const outcomeCount: Record<string, number> = {};
        return (
            <ButtonGroup variant="contained">
                {availableOutcomeTypes.map((outcomeType, idx) => {
                    const prefix = outcomeType.slice(0, 3);
                    outcomeCount[prefix] = (outcomeCount[prefix] || 0) + 1;
                    const numeral =
                        outcomeCount[prefix] > 1
                            ? ` ${outcomeCount[prefix]}`
                            : "";
                    const text = t(
                        `views.home.components.tax_submission_outcome_preview.outcome_types.${prefix.toLowerCase()}`,
                    );
                    return (
                        <Button
                            key={idx}
                            onClick={() =>
                                handleActiveOutcomeTypeChange(outcomeType)
                            }
                            variant={
                                selectedOutcomeType === outcomeType
                                    ? "outlined"
                                    : "contained"
                            }
                        >
                            {`${text}${numeral}`}
                        </Button>
                    );
                })}
            </ButtonGroup>
        );
    }, [
        availableOutcomeTypes,
        handleActiveOutcomeTypeChange,
        selectedOutcomeType,
        t,
    ]);

    return (
        <Box>
            {availableOutcomeTypes.length > 1 && outcomeButtons}
            <TaxSubmissionOutcomePreviewer
                loading={loading}
                file={file}
                governmentAgencyType={governmentAgencyType}
            />
            <Box sx={{ ml: -4 }}>
                <VaporToolbar
                    size="large"
                    variant="regular"
                    contentRight={[
                        file && (
                            <Button
                                key="download"
                                size="large"
                                variant="contained"
                                disabled={isFileDownloading}
                                onClick={handleDownload}
                            >
                                {isFileDownloading ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                ) : (
                                    <VaporIcon
                                        icon={faDownload}
                                        color="inverse"
                                    />
                                )}
                                <Typography sx={{ marginLeft: 1 }}>
                                    {t(
                                        "views.home.components.tax_submission_outcome_preview.download",
                                    )}
                                </Typography>
                            </Button>
                        ),
                    ]}
                />
            </Box>
        </Box>
    );
};
